import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { customersService, Customer } from '../services/customersService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

// Lucide React icons
import {
  Plus,
  Edit,
  Search,
  Users,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

interface CustomerListProps {
  refreshTrigger?: number;
  onCreateClick: () => void;
  onEditClick: (customer: Customer) => void;
}

export const ModernCustomerList: React.FC<CustomerListProps> = ({
  refreshTrigger,
  onCreateClick,
  onEditClick,
}) => {
  const { translate } = useTranslation();
  const { hasPermission } = useRBAC();

  // Permission checks
  const canCreate = hasPermission('customers', 'create');
  const canEdit = hasPermission('customers', 'edit');

  // Pagination state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20, // Reasonable page size for pagination
    total: 0,
  });

  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedOnceRef = useRef(false);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (searchTerm !== debouncedSearchTerm) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Load customers with pagination
  const loadCustomers = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;
      const currentPage = pagination.page;
      const currentPerPage = pagination.perPage;
      const currentSearch = debouncedSearchTerm;

      try {
        if (
          !hasLoadedOnceRef.current &&
          currentPage === 1 &&
          retryCount === 0
        ) {
          setInitialLoading(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const response = await customersService.getCustomers({
          page: currentPage,
          size: currentPerPage,
          q: currentSearch || undefined,
        });

        const customersArray = Array.isArray(response) ? response : [];
        const total = (response as any).__total ?? 0;

        console.log('📊 Customers loaded:', {
          customersCount: customersArray.length,
          total,
          currentPage,
          currentPerPage,
          search: currentSearch,
          sampleCustomer: customersArray[0],
        });

        setCustomers(customersArray);
        setPagination((prev) => ({ ...prev, total }));
        hasLoadedOnceRef.current = true;
        setError(null);
        setIsNetworkError(false);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load customers';
        console.error('Failed to load customers:', err);

        // Check if it's a network error
        const isNetworkErr =
          errorMessage.includes('Network error') ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('ERR_CONNECTION_REFUSED') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Unable to connect');

        if (retryCount < maxRetries && isNetworkErr) {
          await new Promise((resolve) =>
            setTimeout(resolve, (retryCount + 1) * 1000)
          );
          return loadCustomers(retryCount + 1);
        }

        setError(errorMessage);
        setIsNetworkError(isNetworkErr);
        setCustomers([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    },
    [pagination.page, pagination.perPage, debouncedSearchTerm]
  );

  // Load customers when pagination or search changes
  useEffect(() => {
    loadCustomers();
  }, [
    pagination.page,
    pagination.perPage,
    debouncedSearchTerm,
    refreshTrigger,
    loadCustomers,
  ]);

  const handleSearch = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setDebouncedSearchTerm(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(pagination.total / pagination.perPage)
    ) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return '0₫';
    return `${amount.toLocaleString()}₫`;
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" variant="primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between -mx-4 px-4">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.customers.title', 'Customers')}
          </h1>
        </div>
        {canCreate && (
          <Button
            onClick={onCreateClick}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>{translate('pos.common.action.create', 'Create')}</span>
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={translate('common.search', 'Search customers...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch}>
              {translate('common.search', 'Search')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <div className="font-semibold">
                {isNetworkError
                  ? translate('pos.dashboard.network_error', 'Connection Error')
                  : translate(
                      'pos.dashboard.error_loading',
                      'Error loading data'
                    )}
              </div>
              <div>{error}</div>
              {isNetworkError && (
                <div className="text-sm mt-2 space-y-1">
                  <p>
                    {translate(
                      'pos.dashboard.network_error_help',
                      'Please check:'
                    )}
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>
                      {translate(
                        'pos.dashboard.network_error_check_server',
                        'The API server is running'
                      )}
                    </li>
                    <li>
                      {translate(
                        'pos.dashboard.network_error_check_connection',
                        'Your network connection is active'
                      )}
                    </li>
                    <li>
                      {translate(
                        'pos.dashboard.network_error_check_proxy',
                        'The API proxy is configured correctly'
                      )}
                    </li>
                  </ul>
                </div>
              )}
              <Button
                onClick={() => {
                  setError(null);
                  setIsNetworkError(false);
                  loadCustomers();
                }}
                variant="outline"
                size="sm"
                className="mt-3"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {translate('pos.common.action.refresh', 'Retry')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.customers.title', 'Customers')}
            {pagination.total > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({pagination.total} {translate('common.total', 'total')})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" variant="primary" />
            </div>
          )}
          {!loading && customers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {translate(
                'pos.dashboard.no_data_available',
                'No data available'
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.firstName',
                          'First Name'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.lastName',
                          'Last Name'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.email',
                          'Email'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.phone',
                          'Phone'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.totalSpent',
                          'Total Spent'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.nbCommands',
                          'Orders'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.customers.fields.lastSeen',
                          'Last Seen'
                        )}
                      </TableHead>
                      {canEdit && (
                        <TableHead>
                          {translate('pos.common.action.actions', 'Actions')}
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => {
                      // Handle Zalo customers that might have 'name' instead of firstName/lastName
                      const customerAny = customer as any;
                      const firstName =
                        customer.firstName ||
                        (customerAny.name
                          ? customerAny.name.split(' ')[0]
                          : null) ||
                        '-';
                      const lastName =
                        customer.lastName ||
                        (customerAny.name &&
                        customerAny.name.split(' ').length > 1
                          ? customerAny.name.split(' ').slice(1).join(' ')
                          : null) ||
                        '-';

                      return (
                        <TableRow key={customer.id}>
                          <TableCell className="font-medium">
                            <span data-sensitive-field="firstName">
                              {firstName}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span data-sensitive-field="lastName">
                              {lastName}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span data-sensitive-field="email">
                              {customer.email || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span data-sensitive-field="phone">
                              {customerAny.phone || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span data-sensitive-field="amount">
                              {formatCurrency(customer.totalSpent)}
                            </span>
                          </TableCell>
                          <TableCell>{customer.nbCommands || 0}</TableCell>
                          <TableCell>{formatDate(customer.lastSeen)}</TableCell>
                          {canEdit && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onEditClick(customer)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('pos.dashboard.showing', 'Showing')}{' '}
              {Math.min(
                (pagination.page - 1) * pagination.perPage + 1,
                pagination.total
              )}{' '}
              {translate('pos.dashboard.to', 'to')}{' '}
              {Math.min(pagination.page * pagination.perPage, pagination.total)}{' '}
              {translate('pos.dashboard.of', 'of')} {pagination.total}{' '}
              {translate('pos.dashboard.customers', 'customers')}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {translate('common.previous', 'Previous')}
              </Button>
              <span className="text-sm px-2">
                {translate('pos.dashboard.page', 'Page')} {pagination.page}{' '}
                {translate('pos.dashboard.of', 'of')}{' '}
                {Math.ceil(pagination.total / pagination.perPage)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page >=
                  Math.ceil(pagination.total / pagination.perPage)
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate('common.next', 'Next')}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
